import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import CommonRichTextField from '../CommonRichTextField/CommonRichTextField';
import { saveSampleTabData } from '../../Pages/DevlopmentOrder/store/DevelopmentOrder.action';
import { toasterMsg } from '../Toaster/Toaster';
import { InactiveIcon } from '../../bombiix-icon';
import './SampleCommentColumn.scss';

/** Imperative handle so a parent Save button can flush pending edits on demand. */
export interface SampleCommentColumnHandle {
  /** Persists the current value if it changed since the last save. */
  save: () => void;
  /** Whether there is an unsaved edit. */
  hasPendingChanges: () => boolean;
}

export interface SampleCommentColumnProps {
  /** Heading shown above the editor (already translated). */
  title: string;
  /** Sample field this column writes, e.g. `licensingComment`. Doubles as the save section. */
  field: 'licensingComment' | 'customerComment';
  /** Stored comment value (HTML or legacy plain text). */
  value: string;
  /** Sample + order identifiers needed to persist. */
  sampleId: string;
  devOrderId: string;
  /** Disables editing (e.g. archived product). */
  disabled?: boolean;
  /** i18n key for the success toast shown on an explicit Save. */
  savedMessageKey?: string;
  'data-testid'?: string;
}

/**
 * One rich-text comment column (Licensing / Customer) for the Sample Comments tab
 * (BM-9882). Reuses the master {@link CommonRichTextField} editor — its toolbar
 * already matches the Figma reduced set — and adds the column heading and the
 * "Not visible to supplier" toolbar badge.
 *
 * Autosaves its own field on unmount and on `beforeunload`, mirroring the existing
 * `NewComments.js` fitComment editor: the value is written through
 * `saveSampleTabData` with the field name as the section, hitting
 * `PUT /orders-samples/:sampleId/:field`.
 *
 * Suppliers never see this column, so no supplier read-only mode is needed here —
 * the parent gates rendering on `!isSupplierLoggedIn`.
 */
const BADGE_ICON_SIZE = 20;

const SampleCommentColumn = forwardRef<SampleCommentColumnHandle, SampleCommentColumnProps>(function SampleCommentColumn(
  {
    title,
    field,
    value,
    sampleId,
    devOrderId,
    disabled = false,
    savedMessageKey,
    'data-testid': dataTestId,
  },
  ref,
) {
  const { t } = useTranslation('product-sample');
  const dispatch = useDispatch();
  const [html, setHtml] = useState<string>(value ?? '');
  /** Live mirror of the edited value + the last-saved value, so autosave can skip a no-op write. */
  const currentRef = useRef<string>(value ?? '');
  const savedRef = useRef<string>(value ?? '');

  useEffect(() => {
    setHtml(value ?? '');
    currentRef.current = value ?? '';
    savedRef.current = value ?? '';
  }, [value]);

  const handleChange = useCallback((next: string) => {
    currentRef.current = next;
    setHtml(next);
  }, []);

  /**
   * Persists the current value when it changed. `silent` suppresses the success
   * toast — used by blur/unmount autosave; an explicit Save shows the toast.
   *
   * @param {boolean} [silent=true] - When true, no success toaster is shown.
   * @returns {void}
   */
  const persist = useCallback(
    (silent = true) => {
      if (disabled || currentRef.current === savedRef.current) {
        return;
      }
      const nextValue = currentRef.current;
      savedRef.current = nextValue;
      dispatch(saveSampleTabData({ [field]: nextValue }, { devOrderId, section: field, sampleId }, 'tosterOff'));
      if (!silent && savedMessageKey) {
        toasterMsg(t(savedMessageKey), 'success');
      }
    },
    [disabled, dispatch, field, devOrderId, sampleId, savedMessageKey, t],
  );

  const autoSave = useCallback(() => persist(true), [persist]);

  useEffect(
    () => () => {
      autoSave();
    },
    [autoSave],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', autoSave);
    return () => window.removeEventListener('beforeunload', autoSave);
  }, [autoSave]);

  useImperativeHandle(
    ref,
    () => ({
      save: () => persist(false),
      hasPendingChanges: () => !disabled && currentRef.current !== savedRef.current,
    }),
    [persist, disabled],
  );

  const notVisibleText = t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.COMMENT_COLUMNS.NOT_VISIBLE_TO_SUPPLIER');
  const notVisibleBadge = (
    <>
      <span className="bombiix-rich-text__toolbar-suffix-icon" aria-hidden="true">
        <InactiveIcon size={BADGE_ICON_SIZE} />
      </span>
      <span className="bombiix-rich-text__toolbar-suffix-text" title={notVisibleText}>
        {notVisibleText}
      </span>
    </>
  );

  return (
    <div className="sample-comment-column" data-testid={dataTestId}>
      <p className="sample-comment-column__title">{title}</p>
      <CommonRichTextField
        label=""
        value={html}
        onChange={handleChange}
        placeholder={t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.COMMENT_COLUMNS.ENTER_COMMENTS')}
        disabled={disabled}
        toolbarSuffix={notVisibleBadge}
        minHeight="calc(100vh - 350px)"
        {...(dataTestId ? { 'data-testid': `${dataTestId}-editor` } : {})}
      />
    </div>
  );
});

export default SampleCommentColumn;
